import { useEffect, useMemo, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import Modal from "../ui/Modal";
import ConfirmDialog from "../ui/ConfirmDialog";
import FormField, { FormActions, fieldInputStyle } from "../ui/FormField";
import {
  createRoteiro,
  updateRoteiro,
  deleteRoteiro,
  listCandidatosGrupo,
  patchGrupoRoteiro,
} from "../../services/roteirosService";
import { listClientes } from "../../services/clientesService";
import * as formasPagamentoService from "../../services/formasPagamentoService";
import TaxonomyManagerModal from "./TaxonomyManagerModal";

// StatusRoteiro: 0 Rascunho · 1 EmMontagem · 2 Confirmado
const STATUS_OPTIONS = [
  { value: 0, label: "Rascunho" },
  { value: 1, label: "Em montagem" },
  { value: 2, label: "Confirmado" },
];
// MOEDA_OPTIONS removido na Fase 2 — moeda herda do guia (Guia.Moeda).
// FORMA_OPTIONS removida no PR2b — agora vem da API (/api/FormasPagamento).

const EMPTY = {
  titulo: "",
  descricao: "",
  dataInicio: "",
  dataFim: "",
  status: 0,
  clienteId: "",
  // financeiros
  valorBruto: "",
  descontoPercentual: "0",
  jurosPercentual: "0",
  // Fase 2: moeda saiu do form — herda do guia logado.
  formaPagamentoId: "",
  numeroParcelas: "1",
  primeiraParcelaVencimento: "",
  intervaloParcelasDias: "30",
  entradaPercentual: "0",
  entradaData: "",
};

// Replica a fórmula do backend (RoteiroFinanceiroService.ComputeValorFinal)
// para preview ao vivo — o backend é a fonte da verdade no save.
function computeValorFinal(bruto, descPct, jurosPct) {
  const b = parseFloat(String(bruto).replace(",", ".")) || 0;
  if (b <= 0) return { subtotal: 0, descontoValor: 0, jurosValor: 0, final: 0 };
  const d = Math.max(0, Math.min(100, parseFloat(descPct) || 0));
  const j = Math.max(0, parseFloat(jurosPct) || 0);
  const descontoValor = +(b * (d / 100)).toFixed(2);
  const subtotal = +(b - descontoValor).toFixed(2);
  const jurosValor = +(subtotal * (j / 100)).toFixed(2);
  const final = +(subtotal + jurosValor).toFixed(2);
  return { subtotal, descontoValor, jurosValor, final };
}

function symbol(moeda) {
  return moeda === "EUR" ? "€" : moeda === "BRL" ? "R$" : moeda === "CHF" ? "CHF" : "$";
}

function fmt(v, moeda) {
  return `${symbol(moeda)} ${Number(v ?? 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function RoteiroFormModal({ open, roteiro, onClose, onSaved, onDeleted }) {
  const { C } = useTheme();
  const { profile } = useAuth();
  const isEdit = !!roteiro;
  // Fase 2: símbolo do display vem do roteiro (histórico, ao editar) ou do
  // guia logado (na criação). Roteiro já tem moeda gravada — preserva o
  // histórico mesmo se a moeda do guia mudasse no futuro.
  const moedaExibicao = roteiro?.moeda ?? profile?.moeda ?? "EUR";
  const [form, setForm] = useState(EMPTY);
  const [clientes, setClientes] = useState([]);
  const [formasPagamento, setFormasPagamento] = useState([]);
  const [formaManagerOpen, setFormaManagerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [conflito, setConflito] = useState(null); // { mensagem, dias: [...] }
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Confirmação quando o usuário encurta o período e dias existentes serão apagados.
  // Formato: { payload, dropDias: [numerosOrdenados...] } ou null.
  const [confirmReduce, setConfirmReduce] = useState(null);
  // Datas originais do roteiro — usadas pra restaurar o form se o usuário
  // cancelar o ConfirmDialog de redução (evita deixar o user com estado que
  // levaria à perda de dados se salvar por engano).
  const [prevDatas, setPrevDatas] = useState({ inicio: "", fim: "" });
  // ─── Grupo (viagens compartilhadas com múltiplos contratos) ───
  // grupoId local shadowa roteiro.grupoId enquanto o modal está aberto — sync
  // com backend acontece via patchGrupoRoteiro (PATCH dedicado, não pelo PUT).
  const [grupoId, setGrupoId] = useState(null);
  const [candidatos, setCandidatos] = useState([]);
  const [grupoLoading, setGrupoLoading] = useState(false);
  const [grupoError, setGrupoError] = useState(null);

  useEffect(() => {
    if (!open) return;
    listClientes()
      .then(setClientes)
      .catch(() => setClientes([]));
    // PR2b — formas de pagamento via API
    formasPagamentoService.listFormasPagamento()
      .then(setFormasPagamento)
      .catch(() => setFormasPagamento([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (roteiro) {
      setForm({
        titulo: roteiro.titulo ?? "",
        descricao: roteiro.descricao ?? "",
        dataInicio: roteiro.dataInicio ?? "",
        dataFim: roteiro.dataFim ?? "",
        status: roteiro.status ?? 0,
        clienteId: roteiro.clienteId != null ? String(roteiro.clienteId) : "",
        valorBruto: roteiro.valorBruto != null ? String(roteiro.valorBruto) : "",
        descontoPercentual: String(roteiro.descontoPercentual ?? 0),
        jurosPercentual: String(roteiro.jurosPercentual ?? 0),
        // PR2b: usa o ID do DTO (resolvido pelo backend com FK > Legado).
        formaPagamentoId:
          roteiro.formaPagamentoId != null ? String(roteiro.formaPagamentoId) : "",
        numeroParcelas: String(roteiro.numeroParcelas ?? 1),
        primeiraParcelaVencimento: roteiro.primeiraParcelaVencimento ?? "",
        intervaloParcelasDias: String(roteiro.intervaloParcelasDias ?? 30),
        entradaPercentual: String(roteiro.entradaPercentual ?? 0),
        entradaData: roteiro.entradaData ?? "",
      });
      setPrevDatas({
        inicio: roteiro.dataInicio ?? "",
        fim: roteiro.dataFim ?? "",
      });
      setGrupoId(roteiro.grupoId ?? null);
    } else {
      setForm(EMPTY);
      setPrevDatas({ inicio: "", fim: "" });
      setGrupoId(null);
    }
    setError(null);
    setConflito(null);
    setConfirmReduce(null);
    setGrupoError(null);
    setCandidatos([]);
  }, [open, roteiro]);

  // Carrega candidatos a vínculo — só faz sentido em edição (roteiro existente).
  useEffect(() => {
    if (!open || !roteiro?.id) return;
    listCandidatosGrupo(roteiro.id)
      .then(setCandidatos)
      .catch(() => setCandidatos([]));
  }, [open, roteiro?.id]);

  async function handleVincularGrupo(outroId) {
    if (!roteiro?.id) return;
    setGrupoLoading(true);
    setGrupoError(null);
    try {
      await patchGrupoRoteiro(roteiro.id, outroId);
      // Otimista: assume o mesmo GrupoId do candidato selecionado. Se ele era
      // standalone, backend gerou novo Guid — recarrega candidatos pra refletir.
      const cand = candidatos.find((c) => c.id === outroId);
      setGrupoId(cand?.grupoId ?? "vinculado"); // placeholder até o próximo refresh
      onSaved?.(); // pai recarrega listagem → próxima abertura traz GrupoId real
    } catch (err) {
      setGrupoError(err?.response?.data?.message || "Erro ao vincular ao grupo.");
    } finally {
      setGrupoLoading(false);
    }
  }

  async function handleDesvincularGrupo() {
    if (!roteiro?.id) return;
    setGrupoLoading(true);
    setGrupoError(null);
    try {
      await patchGrupoRoteiro(roteiro.id, null);
      setGrupoId(null);
      onSaved?.();
    } catch (err) {
      setGrupoError(err?.response?.data?.message || "Erro ao desvincular.");
    } finally {
      setGrupoLoading(false);
    }
  }

  // Irmãos do grupo atual (candidatos com o mesmo grupoId).
  const irmaos = grupoId
    ? candidatos.filter((c) => c.grupoId === grupoId)
    : [];

  // Cálculo derivado em tempo real — mostrado logo abaixo dos campos financeiros
  const calc = useMemo(
    () => computeValorFinal(form.valorBruto, form.descontoPercentual, form.jurosPercentual),
    [form.valorBruto, form.descontoPercentual, form.jurosPercentual]
  );
  const numParcelas = Math.max(1, parseInt(form.numeroParcelas, 10) || 1);
  const entradaPct = Math.max(0, Math.min(100, parseFloat(form.entradaPercentual) || 0));
  const entradaValor = calc.final > 0 ? +((calc.final * entradaPct) / 100).toFixed(2) : 0;
  const saldo = Math.max(0, +(calc.final - entradaValor).toFixed(2));
  const valorPorParcela = saldo > 0 ? +(saldo / numParcelas).toFixed(2) : 0;

  function update(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function close() {
    if (saving || deleting) return;
    onClose?.();
  }

  function computeDaysInclusive(inicio, fim) {
    if (!inicio || !fim) return 0;
    const a = new Date(inicio);
    const b = new Date(fim);
    return Math.floor((b - a) / (1000 * 60 * 60 * 24)) + 1;
  }

  function buildPayload() {
    return {
      titulo: form.titulo.trim(),
      descricao: form.descricao.trim() || null,
      dataInicio: form.dataInicio,
      dataFim: form.dataFim,
      status: Number(form.status),
      clienteId: form.clienteId ? Number(form.clienteId) : null,
      valorBruto: form.valorBruto
        ? parseFloat(String(form.valorBruto).replace(",", "."))
        : null,
      descontoPercentual: parseFloat(form.descontoPercentual) || 0,
      jurosPercentual: parseFloat(form.jurosPercentual) || 0,
      // PR2b: envia ID (int). Backend prefere ID quando ambos vêm.
      formaPagamentoId: form.formaPagamentoId ? Number(form.formaPagamentoId) : null,
      numeroParcelas: numParcelas,
      primeiraParcelaVencimento: form.primeiraParcelaVencimento || null,
      intervaloParcelasDias: parseInt(form.intervaloParcelasDias, 10) || 30,
      entradaPercentual: entradaPct,
      entradaData: form.entradaData || null,
    };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.titulo.trim()) {
      setError("O título é obrigatório.");
      return;
    }
    if (!form.dataInicio || !form.dataFim) {
      setError("Informe data de início e fim.");
      return;
    }
    if (form.dataFim < form.dataInicio) {
      setError("A data final não pode ser anterior à inicial.");
      return;
    }

    const payload = buildPayload();

    // Edição que encurta o período: se existem dias com NumeroDia fora do novo
    // intervalo, confirma com o usuário antes de enviar o PUT (backend apaga).
    if (isEdit && roteiro.dias?.length > 0) {
      const newDuration = computeDaysInclusive(form.dataInicio, form.dataFim);
      const dropDias = roteiro.dias
        .filter((d) => d.numeroDia > newDuration)
        .map((d) => d.numeroDia)
        .sort((a, b) => a - b);
      if (dropDias.length > 0) {
        setConfirmReduce({ payload, dropDias });
        return;
      }
    }

    await doSave(payload);
  }

  async function doSave(payload) {
    setSaving(true);
    setError(null);
    setConflito(null);
    try {
      if (isEdit) {
        await updateRoteiro(roteiro.id, payload);
      } else {
        await createRoteiro(payload);
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      // 409 Conflict = datas ocupadas; o body é um DatasConflitantesDto
      if (err?.response?.status === 409 && err.response.data?.dias) {
        setConflito(err.response.data);
      } else {
        setError(
          err?.response?.data?.message ||
            err?.response?.data?.errors?.join?.(" ") ||
            "Erro ao salvar roteiro."
        );
      }
    } finally {
      setSaving(false);
    }
  }

  function cancelReduce() {
    setForm((f) => ({ ...f, dataInicio: prevDatas.inicio, dataFim: prevDatas.fim }));
    setConfirmReduce(null);
  }

  async function confirmReduceAndSave() {
    const payload = confirmReduce.payload;
    setConfirmReduce(null);
    await doSave(payload);
  }

  async function handleDelete() {
    if (!isEdit) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteRoteiro(roteiro.id);
      setConfirmDelete(false);
      onDeleted?.();
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Erro ao excluir roteiro.");
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  }

  const inputStyle = fieldInputStyle(C);
  const sectionLabelStyle = {
    fontSize: 11,
    fontWeight: 700,
    color: C.accent,
    textTransform: "uppercase",
    letterSpacing: 1,
    marginTop: 20,
    marginBottom: 10,
    paddingBottom: 6,
    borderBottom: `1px solid ${C.border}`,
  };

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={isEdit ? "Editar Roteiro" : "Novo Roteiro"}
        subtitle={isEdit ? roteiro?.titulo : "Monte um novo itinerário de viagem"}
        width={680}
      >
        <form onSubmit={handleSubmit}>
          {/* ─── Seção 1 — Informações básicas ─── */}
          <div style={sectionLabelStyle}>Informações</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 14,
            }}
          >
            <FormField label="Título" required span={2}>
              <input
                autoFocus
                value={form.titulo}
                onChange={(e) => update("titulo", e.target.value)}
                style={inputStyle}
                placeholder="Ex: Bernina Express + Lago di Como"
              />
            </FormField>

            <FormField label="Cliente" span={2}>
              <select
                value={form.clienteId}
                onChange={(e) => update("clienteId", e.target.value)}
                style={inputStyle}
              >
                <option value="">(Sem cliente)</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Data de Início" required>
              <input
                type="date"
                value={form.dataInicio}
                onChange={(e) => update("dataInicio", e.target.value)}
                style={inputStyle}
              />
            </FormField>

            <FormField label="Data de Fim" required>
              <input
                type="date"
                value={form.dataFim}
                onChange={(e) => update("dataFim", e.target.value)}
                style={inputStyle}
              />
            </FormField>

            <FormField label="Status" span={2}>
              <select
                value={form.status}
                onChange={(e) => update("status", e.target.value)}
                style={inputStyle}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Descrição" span={2}>
              <textarea
                value={form.descricao}
                onChange={(e) => update("descricao", e.target.value)}
                rows={6}
                style={{ ...inputStyle, resize: "vertical", fontFamily: "'DM Sans', sans-serif" }}
                placeholder="Resumo do roteiro, destinos principais..."
              />
            </FormField>
          </div>

          {/* ─── Grupo (viagens compartilhadas) ───
              Só em edição — na criação, o roteiro nasce standalone. Pra criar
              grupo direto, use "Duplicar como grupo" na listagem. */}
          {isEdit && (
            <>
              <div style={sectionLabelStyle}>Grupo</div>
              <div style={{ fontSize: 11, color: C.textDim, marginTop: -4, marginBottom: 10, lineHeight: 1.5 }}>
                Vincule 2 ou mais roteiros que representam a MESMA viagem física
                com contratos separados (2 famílias viajando juntas, por exemplo).
                Assim eles podem compartilhar as mesmas datas sem conflito de agenda.
              </div>

              {grupoId ? (
                <div
                  style={{
                    padding: "12px 14px",
                    background: C.bgCardHover,
                    border: `1px solid ${C.accent}`,
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    marginBottom: 8,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, marginBottom: 4 }}>
                      ✓ VINCULADO A UM GRUPO
                    </div>
                    {irmaos.length > 0 ? (
                      <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>
                        Compartilha datas com:{" "}
                        {irmaos.map((i, idx) => (
                          <span key={i.id} style={{ fontWeight: 600 }}>
                            {idx > 0 && ", "}
                            {i.titulo}
                            {i.clienteNome && ` (${i.clienteNome})`}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: C.textMuted }}>
                        Faz parte de um grupo — irmãos vêm da API na próxima
                        recarga da listagem.
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleDesvincularGrupo}
                    disabled={grupoLoading}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 8,
                      border: `1px solid ${C.border}`,
                      background: "transparent",
                      color: C.textMuted,
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: grupoLoading ? "wait" : "pointer",
                      fontFamily: "'DM Sans', sans-serif",
                      flexShrink: 0,
                    }}
                  >
                    Desvincular
                  </button>
                </div>
              ) : (
                <div style={{ marginBottom: 8 }}>
                  {candidatos.length === 0 ? (
                    <div
                      style={{
                        padding: "10px 12px",
                        border: `1px dashed ${C.border}`,
                        borderRadius: 8,
                        fontSize: 12,
                        color: C.textDim,
                      }}
                    >
                      Nenhum roteiro compatível encontrado (mesmo cliente ou datas
                      próximas). Use "Duplicar como grupo" na listagem pra criar um
                      irmão a partir deste.
                    </div>
                  ) : (
                    <>
                      <label style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, display: "block", marginBottom: 6 }}>
                        Vincular a outro roteiro:
                      </label>
                      <select
                        defaultValue=""
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v) handleVincularGrupo(Number(v));
                          e.target.value = "";
                        }}
                        disabled={grupoLoading}
                        style={inputStyle}
                      >
                        <option value="">Selecione um roteiro...</option>
                        {candidatos.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.titulo} · {c.dataInicio} → {c.dataFim}
                            {c.clienteNome && ` · ${c.clienteNome}`}
                            {c.grupoId ? " · [em grupo]" : ""}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                </div>
              )}

              {grupoError && (
                <div
                  style={{
                    padding: "8px 12px",
                    background: C.redDim,
                    color: C.red,
                    borderRadius: 8,
                    fontSize: 11,
                    fontWeight: 500,
                    marginBottom: 8,
                  }}
                >
                  {grupoError}
                </div>
              )}
            </>
          )}

          {/* ─── Seção 2 — Financeiro ─── */}
          <div style={sectionLabelStyle}>Financeiro</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 14,
            }}
          >
            <FormField label="Valor Bruto">
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.valorBruto}
                onChange={(e) => update("valorBruto", e.target.value)}
                style={inputStyle}
                placeholder="0,00"
              />
            </FormField>

            {/* Fase 2: campo Moeda removido — herda do guia logado. */}

            <FormField label="Forma de Pagamento">
              <div style={{ display: "flex", gap: 8 }}>
                <select
                  value={form.formaPagamentoId}
                  onChange={(e) => update("formaPagamentoId", e.target.value)}
                  style={{ ...inputStyle, flex: 1 }}
                >
                  <option value="">Selecione...</option>
                  {formasPagamento.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.nome}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setFormaManagerOpen(true)}
                  title="Gerenciar formas de pagamento"
                  style={{
                    padding: "11px 14px",
                    borderRadius: 10,
                    border: `1px solid ${C.accent}`,
                    background: "transparent",
                    color: C.accent,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: "'DM Sans', sans-serif",
                    whiteSpace: "nowrap",
                  }}
                >
                  + Gerenciar
                </button>
              </div>
            </FormField>

            <FormField label="Desconto (%)">
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={form.descontoPercentual}
                onChange={(e) => update("descontoPercentual", e.target.value)}
                style={inputStyle}
                placeholder="0"
              />
            </FormField>

            <FormField label="Desconto em valor" hint="Calculado automaticamente">
              <input
                value={fmt(calc.descontoValor, moedaExibicao)}
                readOnly
                tabIndex={-1}
                style={{ ...inputStyle, opacity: 0.7, cursor: "default" }}
              />
            </FormField>

            <FormField label="Juros parcelamento (%)">
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.jurosPercentual}
                onChange={(e) => update("jurosPercentual", e.target.value)}
                style={inputStyle}
                placeholder="0"
              />
            </FormField>

            <FormField label="Entrada (%)" hint="0 = sem entrada">
              <input
                type="number"
                step="1"
                min="0"
                max="100"
                value={form.entradaPercentual}
                onChange={(e) => update("entradaPercentual", e.target.value)}
                style={inputStyle}
                placeholder="30"
              />
            </FormField>

            <FormField label="Valor da entrada" hint="Calculado automaticamente">
              <input
                value={fmt(entradaValor, moedaExibicao)}
                readOnly
                tabIndex={-1}
                style={{ ...inputStyle, opacity: 0.7, cursor: "default" }}
              />
            </FormField>

            <FormField label="Data da entrada">
              <input
                type="date"
                value={form.entradaData}
                onChange={(e) => update("entradaData", e.target.value)}
                style={inputStyle}
              />
            </FormField>

            <FormField label="Nº de Parcelas (saldo)">
              <input
                type="number"
                min="1"
                max="60"
                value={form.numeroParcelas}
                onChange={(e) => update("numeroParcelas", e.target.value)}
                style={inputStyle}
              />
            </FormField>

            <FormField label="1ª Parcela vence em">
              <input
                type="date"
                value={form.primeiraParcelaVencimento}
                onChange={(e) => update("primeiraParcelaVencimento", e.target.value)}
                style={inputStyle}
              />
            </FormField>

            <FormField label="Intervalo (dias)">
              <input
                type="number"
                min="1"
                value={form.intervaloParcelasDias}
                onChange={(e) => update("intervaloParcelasDias", e.target.value)}
                style={inputStyle}
              />
            </FormField>
          </div>

          {/* Preview financeiro ao vivo */}
          {calc.final > 0 && (
            <div
              style={{
                marginTop: 14,
                padding: "14px 16px",
                background: C.bg,
                borderRadius: 10,
                border: `1px solid ${C.accent}40`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 20,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 10, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Subtotal
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginTop: 2 }}>
                      {fmt(calc.subtotal, moedaExibicao)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Juros
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginTop: 2 }}>
                      {fmt(calc.jurosValor, moedaExibicao)}
                    </div>
                  </div>
                  {entradaValor > 0 && (
                    <div>
                      <div style={{ fontSize: 10, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Saldo
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginTop: 2 }}>
                        {fmt(saldo, moedaExibicao)}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 10, color: C.accent, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>
                    Valor Final
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: C.accent, marginTop: 2 }}>
                    {fmt(calc.final, moedaExibicao)}
                  </div>
                </div>
              </div>
              {/* Linha de cronograma — "Entrada €390,00 + 8x €113,75" */}
              <div
                style={{
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: `1px solid ${C.border}`,
                  fontSize: 13,
                  color: C.text,
                  fontWeight: 600,
                }}
              >
                {entradaValor > 0 ? (
                  <>
                    <span style={{ color: C.accent }}>Entrada:</span> {fmt(entradaValor, moedaExibicao)}{" "}
                    <span style={{ color: C.textMuted }}>+</span> {numParcelas}x {fmt(valorPorParcela, moedaExibicao)}
                  </>
                ) : (
                  <>
                    {numParcelas}x {fmt(valorPorParcela, moedaExibicao)}
                  </>
                )}
              </div>
            </div>
          )}

          {Number(form.status) === 2 && (
            <div
              style={{
                marginTop: 12,
                fontSize: 11,
                color: C.textMuted,
                fontStyle: "italic",
              }}
            >
              💡 Ao confirmar, o sistema gerará automaticamente um lançamento no
              Financeiro com as parcelas e reservará os dias na Agenda.
            </div>
          )}

          {/* ─── Alertas de erro ─── */}
          {conflito && (
            <div
              style={{
                marginTop: 16,
                padding: "14px 16px",
                background: C.redDim,
                borderLeft: `3px solid ${C.red}`,
                borderRadius: 8,
              }}
            >
              <div style={{ fontSize: 12, color: C.red, fontWeight: 700, marginBottom: 6 }}>
                ⚠️ Datas em conflito
              </div>
              <div style={{ fontSize: 12, color: C.red, marginBottom: 8 }}>
                {conflito.mensagem}
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: C.red }}>
                {conflito.dias.map((d, i) => (
                  <li key={i}>
                    {d.data.split("-").reverse().join("/")} — {d.status}
                    {d.clienteNome ? ` (${d.clienteNome}` : ""}
                    {d.roteiroTitulo ? ` · ${d.roteiroTitulo})` : d.clienteNome ? ")" : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {error && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 14px",
                background: C.redDim,
                color: C.red,
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {error}
            </div>
          )}

          <FormActions
            onCancel={close}
            onDelete={isEdit ? () => setConfirmDelete(true) : undefined}
            saving={saving}
            submitLabel={isEdit ? "Salvar alterações" : "Criar roteiro"}
          />
        </form>
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        title="Excluir roteiro?"
        message={`Tem certeza que deseja excluir "${roteiro?.titulo}"? Os dias, financeiro gerado e reserva de agenda também serão removidos.`}
        confirmLabel="Excluir"
        danger
        loading={deleting}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      />

      <ConfirmDialog
        open={!!confirmReduce}
        title="Reduzir período do roteiro?"
        message={
          confirmReduce
            ? `Você reduziu o período. ${
                confirmReduce.dropDias.length === 1
                  ? `O Dia ${confirmReduce.dropDias[0]} (e seus dados) será apagado.`
                  : `Os dias ${confirmReduce.dropDias.join(", ")} (e seus dados) serão apagados.`
              } Continuar?`
            : ""
        }
        confirmLabel="Continuar"
        danger
        loading={saving}
        onCancel={cancelReduce}
        onConfirm={confirmReduceAndSave}
      />

      {/* Gerenciador de Formas de Pagamento (PR2b) */}
      <TaxonomyManagerModal
        open={formaManagerOpen}
        onClose={() => setFormaManagerOpen(false)}
        onList={formasPagamentoService.listFormasPagamento}
        onCreate={formasPagamentoService.createFormaPagamento}
        onUpdate={formasPagamentoService.updateFormaPagamento}
        onDelete={formasPagamentoService.deleteFormaPagamento}
        titulo="Gerenciar Formas de Pagamento"
        itemLabel="forma de pagamento"
        onItemCreated={(created) => {
          setForm((f) => ({ ...f, formaPagamentoId: String(created.id) }));
        }}
        onListChanged={() => {
          formasPagamentoService.listFormasPagamento()
            .then(setFormasPagamento)
            .catch(() => {});
        }}
      />
    </>
  );
}
